import fetch from 'cross-fetch';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
  ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { datadogRum } from '@datadog/browser-rum';
import { getCookie } from './analytics/utils';
import { CoreHttpCookies, CoreHttpHeaders } from './request/constants';

const getSuperGraphURL = () => {
  return typeof window !== 'undefined'
    ? process.env.NEXT_PUBLIC_SUPERGRAPH_URL
    : process.env.SERVERSIDE_SUPERGRAPH_URL;
};

const httpLink = new HttpLink({
  uri: getSuperGraphURL(),
  headers: {
    [CoreHttpHeaders.XClientPlatform]: 'web',
    [CoreHttpHeaders.XCallerClientId]: process.env
      .NEXT_PUBLIC_CLIENT_ID as string,
  },
  credentials: 'include',
  fetch,
});

const authLink = new ApolloLink((operation, forward) => {
  const csrfToken = getCookie(CoreHttpCookies.CsrfToken);
  if (csrfToken) {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        [CoreHttpHeaders.XHeaderCsrfToken]: csrfToken,
      },
    }));
  }
  return forward(operation);
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error,
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    const flatError = graphQLErrors.map((error) => {
      return {
        extensions: error.extensions,
        message: error.message,
      };
    });
    datadogRum.addError(
      new Error(
        `Supergraph operation ${operation.operationName} failed due to ${flatError.length} client errors`
      ),
      {
        graphQLErrors: flatError,
      }
    );
    console.log('Logged a graphql error to datadog');
  }

  if (networkError && (networkError as ServerError).statusCode !== 499) {
    datadogRum.addError(
      new Error(
        `Supergraph operation ${operation.operationName} failed due to network errors`
      ),
      {
        networkError,
        stringifiedNetworkError: JSON.stringify(networkError),
      }
    );
  }
});

export const client = new ApolloClient({
  link: from([authLink, errorLink, retryLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});
